<template>
  <div 
    :class="['global-modal', 'fade', { show: show, active: show }]" 
    v-show="show" 
    tabindex="-1"
    role="dialog" 
    
    aria-labelledby="globalModalLabel" 
    aria-hidden="true"
  >
    <div class="modal-overlay" @click.self="close" >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="globalModalLabel">
              {{ title }}
            </h5>
            <button 
              @click="close" 
              type="button" 
              class="btn-close" 
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <slot />
          </div>
          <div class="modal-footer" v-if="$slots.footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GlobalModal',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: "Modal Title",
    },
    size: {
      type: String,
      default: 'md', // sm, md, lg, xl
      validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value)
    }
  },
  setup(props, { emit }) {
    const close = () => {
      emit("close");
    };

    return {
      close,
    };
  },
};
</script>

<style scoped>
.global-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  display: none;
}

.global-modal.show {
  display: block;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 99;
  overflow-y: auto; /* Allow overlay to scroll if needed */
}

.modal-dialog {
  width: 100%;
  max-width: 800px;
  margin: auto;
  transition: transform 0.3s ease-out;
  position: relative;
  max-height: 90vh; /* Limit dialog height */
  display: flex;
  flex-direction: column;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: none;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 100%; /* Take full height of dialog */
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  flex-shrink: 0; /* Prevent header from shrinking */
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
}

.btn-close {
  background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat;
  border: none;
  opacity: 0.7;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0.25rem;
  transition: all 0.2s ease;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-close:hover {
  opacity: 1;
  transform: scale(1.1);
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto; /* Always allow scrolling */
  flex: 1; /* Take remaining space */
  min-height: 0; /* Important for flexbox scrolling */
  max-height: 75vh;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  gap: 0.75rem;
  background: #f8f9fa;
  flex-shrink: 0; /* Prevent footer from shrinking */
}

/* Size variations */
.modal-dialog.modal-sm {
  max-width: 400px;
}

.modal-dialog.modal-md {
  max-width: 500px;
}

.modal-dialog.modal-lg {
  max-width: 800px;
}

.modal-dialog.modal-xl {
  max-width: 1140px;
}

/* Animation for modal appearance */
.global-modal.show .modal-dialog {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Backdrop animation */
.global-modal.show .modal-overlay {
  animation: backdropFadeIn 0.3s ease-out;
}

@keyframes backdropFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Custom scrollbar for modal body */
.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Enhanced Mobile Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
    align-items: flex-start; /* Start from top on mobile */
    padding-top: 2rem; /* Add top padding */
  }
  
  .modal-dialog {
    max-width: 95% !important;
    margin: 0 auto;
    max-height: 85vh; /* Slightly less on mobile */
  }
  
  .modal-header {
    padding: 1rem 1.25rem;
  }
  
  .modal-body {
    padding: 1.25rem;
    /* Remove max-height restriction on mobile */
  }
  
  .modal-footer {
    padding: 0.75rem 1.25rem;
    flex-wrap: wrap;
  }
}

@media (max-width: 576px) {
  .modal-overlay {
    padding: 0.25rem;
    padding-top: 1rem;
  }
  
  .modal-dialog {
    max-width: 98% !important;
    max-height: 90vh; /* More height on very small screens */
  }
  
  .modal-header {
    padding: 0.75rem 1rem;
  }
  
  .modal-title {
    font-size: 1.1rem;
  }
  
  .modal-body {
    padding: 1rem;
    /* No height restrictions - let content flow naturally */
  }
  
  .modal-footer {
    padding: 0.5rem 1rem;
    flex-direction: column;
  }
  
  .modal-footer ::v-deep .btn {
    width: 100%;
    margin: 0.25rem 0;
  }
}

/* Extra small devices */
@media (max-width: 400px) {
  .modal-dialog {
    max-height: 95vh; /* Almost full screen on very small devices */
  }
  
  .modal-body {
    padding: 0.75rem;
  }
}

/* Prevent body scroll when modal is open */
body.modal-open {
  overflow: hidden;
}

/* Ensure modal is always on top of everything */
.global-modal {
  isolation: isolate;
}

.modal-overlay {
  isolation: isolate;
}

.modal-dialog {
  isolation: isolate;
}

/* Touch-friendly scrolling for mobile */
.modal-body {
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

/* Ensure content is always accessible */
.modal-content {
  min-height: min-content; /* At least fit content */
}
</style>